import axios from 'axios';
import AuthService from './auth-header';
import {
    INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE,
    isInvoiceCreateUploadAllowed,
    isInvoiceProdMulterAllowed,
} from '@/utils/invoice-filename';
import { resolveInvoiceApiUrl } from '@/utils/invoice-api-url';
// /*dev*/const API_URL = 'https://ats.sattvahuman.in/api/';
// /*prod*/ //const API_URL = 'https://ats.sattvahuman.in/api/';

const API_URL = resolveInvoiceApiUrl()

/** Let axios set multipart boundary; manual Content-Type breaks FormData parsing. */
function multipartAuthHeaders() {
    const headers = AuthService.authHeaderForm()
    delete headers['Content-Type']
    return headers
}

function logFormDataKeys(formData, label = 'invoice', force = false) {
    if (!force && process.env.NODE_ENV !== 'development') return
    if (typeof FormData === 'undefined' || !(formData instanceof FormData)) return
    try {
        const keys = []
        formData.forEach((value, key) => {
            const kind = value instanceof Blob
                ? `Blob(${value.type || 'unknown'}, ${value.size || 0}b)`
                : typeof value
            keys.push(`${key}=${kind}`)
        })
        console.info(`[invoice-service] ${label} FormData keys (${keys.length}):`, keys)
    } catch (e) {
        console.warn(`[invoice-service] ${label} FormData key log failed:`, e)
    }
}

/** Pull a short human message out of Express/HTML error pages (no JWT/stack dump). */
function extractMessageFromHtmlBody(html) {
    if (!html || typeof html !== 'string') return ''
    const tryMatch = (re) => {
        const m = html.match(re)
        return m && m[1] ? m[1].replace(/\s+/g, ' ').trim() : ''
    }
    const fromPre = tryMatch(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
    const fromTitle = tryMatch(/<title[^>]*>([\s\S]*?)<\/title>/i)
    const fromH1 = tryMatch(/<h1[^>]*>([\s\S]*?)<\/h1>/i)
    const candidate = [fromPre, fromH1, fromTitle]
        .map((s) => s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim())
        .find((s) => s && s.length > 2 && !/^error$/i.test(s))
    if (!candidate) return ''
    // Drop huge stack traces; keep first meaningful line.
    const firstLine = candidate.split(/\n|\r/).map((l) => l.trim()).find(Boolean) || candidate
    return firstLine.length > 220 ? `${firstLine.slice(0, 220)}…` : firstLine
}

/** True when axios never got an HTTP response (DNS/TCP/firewall/down host). */
export function isInvoiceNetworkFailure(err) {
    if (!err || typeof err !== 'object') return false
    if (err.response) return false
    const code = String(err.code || '').toUpperCase()
    if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ECONNREFUSED'
        || code === 'ENOTFOUND' || code === 'ETIMEDOUT' || code === 'ECONNRESET') {
        return true
    }
    const msg = String(err.message || '')
    // Axios defaults: "Network Error" / "timeout of Nms exceeded" — not our toast copy.
    return /^Network Error$/i.test(msg) || /timeout of \d+ms exceeded/i.test(msg)
}

/** Human toast when POST never reaches / never hears from the API host. */
function formatInvoiceNetworkError(err) {
    const hostHint = (() => {
        try {
            return new URL(API_URL).host
        } catch (e) {
            return API_URL || 'API host'
        }
    })()
    const code = err && err.code ? String(err.code).toUpperCase() : ''
    if (code === 'ECONNABORTED' || /timeout/i.test(String(err && err.message || ''))) {
        return `Cannot reach invoice API at ${hostHint} (timed out). Check that the backend is running and port 5000 is open on your network.`
    }
    return `Cannot reach invoice API at ${hostHint}. Check that the backend is running and reachable on your network (connection timed out / refused).`
}

/**
 * Keep invoiceFile only when it matches the frontend allow-list
 * (pdf/png/jpg/txt/xls/xlsx/csv). Backend multer must allow Excel/CSV too.
 */
function sanitizeInvoiceCreateFormData(formData) {
    if (typeof FormData === 'undefined' || !(formData instanceof FormData)) return formData
    if (typeof formData.getAll !== 'function') return formData
    const files = formData.getAll('invoiceFile')
    if (!files.length) return formData
    const kept = files.filter(isInvoiceCreateUploadAllowed)
    if (kept.length === files.length) return formData
    formData.delete('invoiceFile')
    kept.forEach((file) => {
        const name = (file && file.name) || 'invoice.pdf'
        formData.append('invoiceFile', file, name)
    })
    if (!kept.length) {
        formData.delete('document_type')
        console.warn(
            '[invoice-service] invoiceFile omitted on create — type not allowed (pdf, png, jpg, txt, xls, xlsx, csv)',
        )
    }
    return formData
}

/**
 * New-only create keys that current prod POST /invoice does not persist
 * (item 3–7 never deployed). Sending them can 500 if the old handler
 * inserts unknown columns. Excel/CSV invoiceFile is stripped separately.
 */
const PROD_CREATE_STRIP_KEYS = new Set([
    'layout_style',
    'line_item_preset_id',
    'line_item_column_config',
    'line_item_column_config_table2',
    'line_items_json',
    'include_msme_terms',
    'terms_text',
    'template_id',
    'template_version',
    'company_logo_url',
    'companyLogo',
    'use_second_line_items_table',
    'line_item_table_heading_1',
    'line_item_table_heading_2',
    'second_table_columns_same',
    'location_id',
    'locationId',
    'file',
    'supporting_document',
    'supporting_documents',
    'notify',
    'create_notify',
    'notify_finance',
])

const LINE_ITEM_KEY_RE = /^lineItems\[(\d+)\]\[(\w+)\]$/
const CANDIDATE_KEY_RE = /^candidates\[(\d+)\]\[(\w+)\]$/
const LINE_ITEM_NUMERIC_FIELDS = new Set([
    'quantity', 'unitPrice', 'unit_price', 'amount', 'baseAmount', 'base_amount',
    'rate', 'gst', 'gst_amount', 'igstAmount', 'igstRate', 'taxableValue',
    'lineTotal', 'tds_amount', 'payoutRate', 'ctc', 'presentDays',
    'totalHeadCount', 'dayToMonthRatio', 'monthToDayConversion', 'rateCard',
])

function coerceLineItemField(field, value) {
    if (value === undefined || value === null) return undefined
    const numeric = LINE_ITEM_NUMERIC_FIELDS.has(field)
        || /amount|rate|qty|quantity|price|gst|tds|ctc|days|ratio|count/i.test(field)
    if (numeric) {
        if (value === '') return 0
        const n = typeof value === 'number' ? value : parseFloat(value)
        return Number.isFinite(n) ? n : 0
    }
    if (value === '') return undefined
    return value
}

function isBlobLike(value) {
    return typeof Blob !== 'undefined' && value instanceof Blob
}

function parseMaybeJson(value) {
    if (typeof value !== 'string') return value
    const s = value.trim()
    if (!s.startsWith('{') && !s.startsWith('[')) return value
    try {
        return JSON.parse(s)
    } catch (e) {
        return value
    }
}

function coerceCreateScalar(key, value) {
    if (value === undefined || value === null) return value
    const numericKeys = new Set([
        'base_amount', 'gst', 'gst_amount', 'amount_with_gst', 'total_amount',
        'tds_amount', 'tds_percentage',
    ])
    if (numericKeys.has(key)) {
        const n = typeof value === 'number' ? value : parseFloat(value)
        return Number.isFinite(n) ? n : 0
    }
    return value
}

/**
 * Shape POST /invoice for the *currently deployed* old create:
 * JSON when no prod-safe file; multipart with invoiceFile only when the
 * file is pdf/png/jpg/txt. Never require a supporting document.
 */
function toProdCompatibleCreatePayload(data) {
    if (typeof FormData === 'undefined' || !(data instanceof FormData)) {
        if (!isPlainObject(data)) return { body: data, isMultipart: false }
        const obj = {}
        Object.entries(data).forEach(([key, value]) => {
            if (PROD_CREATE_STRIP_KEYS.has(key)) return
            if (key === 'invoiceFile' || key === 'document_type') return
            if (value === undefined || value === null || value === '') return
            obj[key] = coerceCreateScalar(key, value)
        })
        return { body: obj, isMultipart: false }
    }

    const scalars = {}
    const lineItems = []
    const candidates = []
    let invoiceFile = null
    let documentType = ''

    data.forEach((value, key) => {
        if (PROD_CREATE_STRIP_KEYS.has(key)) return
        if (key === 'invoiceFile') {
            if (isBlobLike(value) && isInvoiceProdMulterAllowed(value)) {
                invoiceFile = value
            }
            return
        }
        if (key === 'document_type') {
            documentType = String(value || '').trim()
            return
        }
        if (isBlobLike(value)) return

        const lineMatch = key.match(LINE_ITEM_KEY_RE)
        if (lineMatch) {
            const idx = Number(lineMatch[1])
            if (!lineItems[idx]) lineItems[idx] = {}
            const coerced = coerceLineItemField(lineMatch[2], value)
            if (coerced !== undefined) lineItems[idx][lineMatch[2]] = coerced
            return
        }
        const candMatch = key.match(CANDIDATE_KEY_RE)
        if (candMatch) {
            const idx = Number(candMatch[1])
            if (!candidates[idx]) candidates[idx] = {}
            candidates[idx][candMatch[2]] = value
            return
        }
        if (value === undefined || value === null || value === '') return
        scalars[key] = coerceCreateScalar(key, value)
    })

    const compactLines = lineItems.filter(Boolean)
    if (compactLines.length) scalars.lineItems = compactLines
    const compactCandidates = candidates.filter(Boolean)
    if (compactCandidates.length && scalars.candidates == null) {
        scalars.candidates = compactCandidates
    } else if (typeof scalars.candidates === 'string') {
        scalars.candidates = parseMaybeJson(scalars.candidates)
    }

    if (invoiceFile) {
        const formData = new FormData()
        Object.entries(scalars).forEach(([key, value]) => {
            if (key === 'lineItems' && Array.isArray(value)) {
                value.forEach((row, index) => {
                    if (!row) return
                    Object.entries(row).forEach(([field, fieldVal]) => {
                        const coerced = coerceLineItemField(field, fieldVal)
                        if (coerced === undefined) return
                        formData.append(`lineItems[${index}][${field}]`, coerced)
                    })
                })
                return
            }
            if (key === 'candidates' && (Array.isArray(value) || isPlainObject(value))) {
                formData.append('candidates', JSON.stringify(value))
                return
            }
            formData.append(key, value)
        })
        const fileName = (invoiceFile && invoiceFile.name) || 'invoice.pdf'
        formData.append('invoiceFile', invoiceFile, fileName)
        formData.append('document_type', documentType || 'Tax Invoice')
        return { body: formData, isMultipart: true }
    }

    return { body: scalars, isMultipart: false }
}

/** Clear toast when multer fileFilter rejects xlsx/doc/etc. */
function formatFileTypeRejectedMessage(raw) {
    const text = String(raw || '')
    if (/Allowed:\s*pdf/i.test(text)
        || /file type not allowed/i.test(text)
        || /fileFilter/i.test(text)) {
        return INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE
    }
    return null
}

/** Clear toast when multer rejects an undeclared multipart file field name. */
function formatMulterUnexpectedFieldMessage(raw) {
    const text = String(raw || '')
    if (!/MulterError|Unexpected field/i.test(text)) return null
    if (!/Unexpected field/i.test(text)) {
        return 'File upload was rejected by the server. Retry with a single PDF under the invoiceFile field, or create without an attachment.'
    }
    return (
        'Server rejected the uploaded file field name (Multer: Unexpected field). '
        + 'POST /api/invoice accepts invoiceFile only — not file, supporting_document, or supporting_documents[]. '
        + 'Remove extra attachments or retry without a file.'
    )
}

/** Prefer JSON message; strip HTML-only 500 pages to a short usable toast. */
export function formatInvoiceApiError(resOrErr, fallback = 'Error creating invoice') {
    // No HTTP response → network / unreachable host (not a FormData validation 500).
    if (isInvoiceNetworkFailure(resOrErr)) {
        return formatInvoiceNetworkError(resOrErr)
    }

    const axiosRes = resOrErr && resOrErr.response ? resOrErr.response : resOrErr
    const data = axiosRes && axiosRes.data !== undefined
        ? axiosRes.data
        : (resOrErr && resOrErr.data !== undefined ? resOrErr.data : resOrErr)
    const status = (axiosRes && axiosRes.status)
        || (resOrErr && resOrErr.status)
        || (resOrErr && resOrErr.httpStatus)
        || null

    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const msg = data.message || data.error || data.msg
            || (Array.isArray(data.errors) && data.errors.length ? data.errors.join(' ') : null)
        if (msg && String(msg).trim()) {
            // Bare axios "Network Error" sometimes lands here via callers wrapping { message }.
            if (/^Network Error$/i.test(String(msg)) && !status) {
                return formatInvoiceNetworkError(resOrErr)
            }
            const typeMsg = formatFileTypeRejectedMessage(msg)
            if (typeMsg) return typeMsg
            const multerMsg = formatMulterUnexpectedFieldMessage(msg)
            if (multerMsg) return multerMsg
            return String(msg).trim()
        }
    }
    if (typeof data === 'string' && data.trim()) {
        const isHtml = /^\s*<!DOCTYPE/i.test(data) || /<html[\s>]/i.test(data) || /<\/?(pre|title|h1)[\s>]/i.test(data)
        if (isHtml) {
            const extracted = extractMessageFromHtmlBody(data)
            if (extracted) {
                console.warn('[invoice-service] HTML error body excerpt:', extracted)
                const typeMsg = formatFileTypeRejectedMessage(extracted)
                if (typeMsg) return typeMsg
                const multerMsg = formatMulterUnexpectedFieldMessage(extracted)
                if (multerMsg) return multerMsg
                if (status === 500) {
                    return `Server error creating invoice — check required fields / logo format. (${extracted})`
                }
                return extracted
            }
        } else {
            const typeMsg = formatFileTypeRejectedMessage(data)
            if (typeMsg) return typeMsg
            const multerMsg = formatMulterUnexpectedFieldMessage(data)
            if (multerMsg) return multerMsg
            const plain = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
            if (plain && plain.length < 280) return plain
        }
        if (status === 500) {
            return 'Server error creating invoice — check required fields / logo format'
        }
        if (status) return `Request failed (${status}). ${fallback}`
    }
    if (typeof resOrErr === 'string' && resOrErr.trim()) {
        const typeMsg = formatFileTypeRejectedMessage(resOrErr)
        if (typeMsg) return typeMsg
        const multerMsg = formatMulterUnexpectedFieldMessage(resOrErr)
        if (multerMsg) return multerMsg
        return resOrErr.trim()
    }
    if (resOrErr && resOrErr.message && !/^Request failed with status code/i.test(resOrErr.message)) {
        if (/^Network Error$/i.test(resOrErr.message) && !status) {
            return formatInvoiceNetworkError(resOrErr)
        }
        const typeMsg = formatFileTypeRejectedMessage(resOrErr.message)
        if (typeMsg) return typeMsg
        const multerMsg = formatMulterUnexpectedFieldMessage(resOrErr.message)
        if (multerMsg) return multerMsg
        return resOrErr.message
    }
    if (status === 500) {
        return 'Server error creating invoice — check required fields / logo format'
    }
    return fallback
}

function isPlainObject(o) {
    return o != null && typeof o === 'object' && !Array.isArray(o);
}

function assignDefinedNonEmpty(target, source) {
    if (!isPlainObject(source)) return;
    for (const [k, v] of Object.entries(source)) {
        if (v !== undefined && v !== null && v !== '') {
            target[k] = v;
        }
    }
}

function parseMaybeJsonObject(val) {
    if (typeof val !== 'string') return null;
    const s = val.trim();
    if (!s.startsWith('{') && !s.startsWith('[')) return null;
    try {
        const parsed = JSON.parse(s);
        return isPlainObject(parsed) ? parsed : null;
    } catch (e) {
        return null;
    }
}

function fieldsArrayToObject(arr) {
    const out = {};
    if (!Array.isArray(arr)) return out;
    for (const item of arr) {
        if (!isPlainObject(item)) continue;
        const key = item.key ?? item.name ?? item.label ?? item.field ?? item.title;
        const val = item.value ?? item.val ?? item.text ?? item.content;
        if (key != null && String(key).trim() !== '' && val !== undefined && val !== null && val !== '') {
            out[String(key).trim()] = val;
        }
    }
    return out;
}

/** Map human / OCR labels to keys the invoice UI already understands */
const FRIENDLY_FIELD_MAP = {
    'invoice no': 'invoiceId',
    'invoice number': 'invoiceId',
    'invoice id': 'invoiceId',
    'inv no': 'invoiceId',
    'tax invoice no': 'invoiceId',
    'invoice date': 'invoiceDate',
    'date of invoice': 'invoiceDate',
    'bill date': 'invoiceDate',
    'po number': 'invoicePONumber',
    'p.o. number': 'invoicePONumber',
    'purchase order': 'invoicePONumber',
    'purchase order no': 'invoicePONumber',
    'work order': 'invoicePONumber',
    'gstin': 'gstNumber',
    'buyer gstin': 'gstNumber',
    'customer gstin': 'gstNumber',
    'taxable value': 'baseAmount',
    'taxable amount': 'baseAmount',
    'sub total': 'baseAmount',
    'subtotal': 'baseAmount',
    'grand total': 'totalAmount',
    'total amount': 'totalAmount',
    'total (incl. tax)': 'totalAmount',
    'client name': 'clientName',
    'bill to': 'clientName',
    'buyer': 'clientName',
    'customer name': 'clientName',
    'place of supply': 'placeOfSupply',
};

function applyFriendlyFieldAliases(obj) {
    if (!isPlainObject(obj)) return obj;
    const out = { ...obj };
    for (const [k, v] of Object.entries(obj)) {
        if (v === undefined || v === null || v === '') continue;
        const lk = String(k).toLowerCase().replace(/\s+/g, ' ').trim();
        const canon = FRIENDLY_FIELD_MAP[lk];
        if (canon && (out[canon] === undefined || out[canon] === null || out[canon] === '')) {
            out[canon] = v;
        }
    }
    return out;
}

function absorbParsedLayer(merged, layer) {
    if (layer == null) return;
    if (typeof layer === 'string') {
        const parsed = parseMaybeJsonObject(layer);
        if (parsed) assignDefinedNonEmpty(merged, parsed);
        return;
    }
    if (!isPlainObject(layer)) return;
    assignDefinedNonEmpty(merged, layer);
    if (Array.isArray(layer.fields)) {
        assignDefinedNonEmpty(merged, fieldsArrayToObject(layer.fields));
    }
}

/**
 * Flatten POST /invoice/upload body into one object for field mapping.
 * Supports documented { data: { data: {...} } }, flat { data: {...} }, and common nested keys.
 */
export function getParsedInvoiceFromUploadResponse(res) {
    if (!res || res.data == null) return {};
    const d = res.data;
    const merged = {};

    absorbParsedLayer(merged, d);
    absorbParsedLayer(merged, d.data);
    if (isPlainObject(d.data)) {
        absorbParsedLayer(merged, d.data.data);
        absorbParsedLayer(merged, d.data.parsed);
        absorbParsedLayer(merged, d.data.result);
        absorbParsedLayer(merged, d.data.extracted);
        absorbParsedLayer(merged, d.data.extraction);
        absorbParsedLayer(merged, d.data.invoice);
    }
    absorbParsedLayer(merged, d.parsed);
    absorbParsedLayer(merged, d.result);
    absorbParsedLayer(merged, d.extracted);
    absorbParsedLayer(merged, d.extraction);
    absorbParsedLayer(merged, d.invoice);
    absorbParsedLayer(merged, d.structured);
    absorbParsedLayer(merged, d.payload);

    return applyFriendlyFieldAliases(merged);
}

export function isInvoiceUploadResponseOk(res) {
    if (!res) return false;
    const st = res.status;
    return st === 'OK' || st === 200 || (typeof st === 'string' && String(st).toLowerCase() === 'ok');
}

class InvoiceService {

    async create(data) {
        let payload = data
        try {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                sanitizeInvoiceCreateFormData(data)
            }
            const compatible = toProdCompatibleCreatePayload(data)
            payload = compatible.body
            logFormDataKeys(payload, 'create')
            const headers = compatible.isMultipart
                ? multipartAuthHeaders()
                : AuthService.authHeader()
            const res = await axios.post(API_URL + 'invoice', payload, { headers })
            console.log(res.data)
            return res.data

        }
        catch (err) {
            // Always dump keys + HTML/JSON excerpt on create failure (helps 500 diagnosis).
            logFormDataKeys(payload, 'create-error', true)
            const axiosRes = err && err.response
            if (axiosRes) {
                const payload = axiosRes.data
                if (typeof payload === 'string') {
                    const excerpt = extractMessageFromHtmlBody(payload) || payload.slice(0, 400)
                    console.warn('[invoice-service] create 500/error body excerpt:', excerpt)
                } else {
                    console.warn('[invoice-service] create error response:', {
                        status: axiosRes.status,
                        data: payload,
                    })
                }
                const message = formatInvoiceApiError(axiosRes, 'Server error creating invoice — check required fields / logo format')
                if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                    return { ...payload, status: payload.status || 'ERROR', message, httpStatus: axiosRes.status }
                }
                return { status: 'ERROR', message, httpStatus: axiosRes.status, data: payload }
            }
            const network = isInvoiceNetworkFailure(err)
            return {
                status: 'ERROR',
                message: formatInvoiceApiError(err, 'Server error creating invoice — check required fields / logo format'),
                httpStatus: null,
                networkError: network,
                code: err && err.code ? err.code : undefined,
            }
        }

    }
    async upload(data) {
        try {
            logFormDataKeys(data, 'upload')
            const headers = (typeof FormData !== 'undefined' && data instanceof FormData)
                ? multipartAuthHeaders()
                : AuthService.authHeader()
            const res = await axios.post(API_URL + 'invoice/upload', data, { headers })
            console.log(res.data)
            return res.data

        }
        catch (err) {
            console.log(err.response)
            return err.response
        }

    }

    // `page`/`limit` are OPTIONAL and backward-compatible — every existing
    // call site (InvoiceDashboard.vue, InvoiceTracker.vue) omits them and
    // keeps working exactly as before. They exist so callers that need the
    // FULL invoice history (e.g. invoice-config-service.js's next-invoice-
    // number fallback, which must scan every invoice matching a prefix, not
    // just page 1) can defensively page through results if this endpoint
    // turns out to paginate server-side — see that caller for how it
    // detects/handles both paginated and non-paginated responses.
    //
    // Empty/null/undefined filter values are omitted from the query string.
    // Sending `sd=&ed=&status=&client=` has been observed to 500 this API;
    // other list callers (e.g. enhanced-invoice-service, attendance) already
    // only append defined params.
    async getAll(sd, ed, status, client, category, dateType, page, limit) {
        try {
            const params = {}
            assignDefinedNonEmpty(params, {
                sd,
                ed,
                status,
                client,
                category,
                dateType,
            })
            if (page != null && page !== '') params.page = page
            if (limit != null && limit !== '') params.limit = limit

            const qs = new URLSearchParams(
                Object.entries(params).map(([k, v]) => [k, String(v)])
            ).toString()
            const url = API_URL + 'invoice' + (qs ? '?' + qs : '')

            const res = await axios.get(url,
                {
                    headers: AuthService.authHeader()
                });
            //console.log(res.data)
            return res.data
        }
        catch (err) {
            console.log(err.response)
            return err.response
        }
    }
    async getById(id) {
        try {
            if (id == null || id === '') return null
            const res = await axios.get(API_URL + 'invoice/' + encodeURIComponent(String(id)),
                {
                    headers: AuthService.authHeader()
                });
            //console.log(res.data)
            return res.data
        }
        catch (err) {
            console.log(err.response)
            return err.response
        }
    }

    // Not in use
    async update(id, data) {
        try {
            const headers = (typeof FormData !== 'undefined' && data instanceof FormData)
                ? multipartAuthHeaders()
                : AuthService.authHeader()
            const res = await axios.put(API_URL + 'invoice/' + id, data, { headers });
            //console.log(res.data)
            return res.data
        }
        catch (err) {
            console.log(err.response)
            return err.response
        }
    }
    async updateInvoice(id, data) {
        console.log('Inside Update Invoice')
        try {
            // FormData: omit Content-Type so axios/browser set multipart boundary.
            const headers = (typeof FormData !== 'undefined' && data instanceof FormData)
                ? multipartAuthHeaders()
                : AuthService.authHeader();
            
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                logFormDataKeys(data, `update/${id}`)
            }
            const res = await axios.put(API_URL + 'invoice/update/' + id, data,
                {
                    headers: headers
                });
            console.log(res.data)
            return res.data
        }
        catch (err) {
            console.log(err.response || err)
            // Network / no response: return a structured error so callers do not throw on res.status
            if (isInvoiceNetworkFailure(err) || !err.response) {
                return {
                    status: 'ERROR',
                    message: formatInvoiceApiError(err, 'Invoice update failed'),
                    httpStatus: null,
                }
            }
            const data = err.response.data
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                return {
                    ...data,
                    status: data.status != null ? data.status : err.response.status,
                    httpStatus: err.response.status,
                    message: data.message || data.error || data.msg || formatInvoiceApiError(err.response, 'Invoice update failed'),
                }
            }
            return {
                status: err.response.status,
                data,
                httpStatus: err.response.status,
                message: formatInvoiceApiError(err.response, 'Invoice update failed'),
            }
        }
    }
    ///api/invoice/update/
    // async delete(cid,jid) {
    //     try {
    //         const res = await axios.delete(API_URL + 'interview?candidate_id='+ cid +'&job_id='+ jid,
    //         { 
    //             headers: AuthService.authHeader()
    //         });
    //         //console.log(res.data)
    //         return res.data
    //     }
    //     catch(err) {
    //         console.log(err.response)
    //         return err.response
    //     }
    // }

}

export default new InvoiceService();